
<template>
  <div
    class="modal modal-sign-offs-create fade fixed-end"
    tabindex="-1"
    role="dialog"
  >
    <div
      class="modal-dialog modal-dialog-vertical dw-md-8 dw-lg-6 dw-xl-5"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="drawer-title">
            {{ title }}
          </h2>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>

        <div class="modal-body">
          <div class="mb-3">
            <label class="control-label">Select Client</label>
            <div class="input-group">
              <span class="input-group-text"
                ><i class="fal fa-fw fa-building"></i
              ></span>
              <div class="flex-grow-1">
                <multiselect
                  :show-labels="false"
                  v-model="client"
                  :options="clients"
                  label="company"
                  track-by="id"
                  placeholder=""
                  v-validate="'required'"
                  data-vv-name="client"
                  @select="clientSelected"
                ></multiselect>
              </div>
            </div>
            <help-block field="client" />
          </div>

          <div class="mb-3">
            <label class="control-label">Amount</label>
             <div class="input-group">
              <span class="input-group-text">$</span>
              <div class="flex-grow-1">
                <input
                  name="amount"
                  v-model="newRetainer.amount"
                  type="number"
                  class="form-control"
                  data-vv-validate-on="input|blur"
                  data-vv-as="amount"
                  v-validate="'required|decimal'"
                />
              </div>
            </div>
            <help-block field="amount" />
          </div>

          <div class="mb-3">
            <label class="control-label">Start Date</label>
            <datepicker
              v-model="newRetainer.start"
              name="start"
              input-class="form-control"
              format="dd/MM/yyyy"
              v-validate="'required'"
              data-vv-name="start"
              data-vv-as="start date"
            ></datepicker>
            <help-block field="start" />
          </div>

          <div class="mb-3">
            <label class="control-label">End Date</label>
            <datepicker
              v-model="newRetainer.end"
              name="end"
              input-class="form-control"
              format="dd/MM/yyyy"
              :clear-button="true"
              data-vv-name="end"
              data-vv-as="end date"
            ></datepicker>
            <help-block field="end" />
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-default"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <c-button @click="handleSave" class="btn btn-rounded btn-primary"
              >Save</c-button
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.invalid-feedback {
  display: inline-block;
}
</style>

<style>
.form-control {
  box-shadow: none !important;
}

.form-control[readonly] {
  background-color: transparent !important;
}
</style>

<script>
import { notify } from "utils";
import { cloneDeep } from "lodash";
import Datepicker from "vuejs-datepicker";
import Multiselect from "vue-multiselect";
import { mapActions, mapState, mapGetters } from "vuex";
import { Selectize, LaddaButton } from "components/form/input";
import { helperMethods } from "vx/modules/clients";
import { CModal } from "@chameleon/chameleon-vue";

export default {
  $_veeValidate: {
    validator: "new",
  },
  props: ["title", "retainer", "mode"],
  extends: CModal,
  components: {
    Selectize,
    LaddaButton,
    Datepicker,
    Multiselect,
  },

  data() {
    return {
      newRetainer: {},
      client: null,
    };
  },

  mounted() {
    this.getClients();

    this.addEventListener('open', () => {
      this.newRetainer = cloneDeep(this.retainer);

      if (
        this.newRetainer
      ) {
        this.client = this.getClientById(this.newRetainer.client_id);
      }
    });
  },

  computed: {
    ...mapState("clientsList", ["clients"]),
    ...mapGetters("clientsList", { getClientById: "getById" })
  },
  methods: {
    ...helperMethods,
    ...mapActions("retainer", ["addSingle", "updateSingle"]),
    ...mapActions("clientsList", ["getClients"]),

    clientSelected(selected) {
      this.newRetainer.client_id = selected.id
    },

    handleSave() {
      this.newRetainer.end = this.newRetainer.end || null;

      if (this.newRetainer.id) {
        return this.handleUpdate();
      } else {
        return this.handleAdd();
      }
    },

    async handleAdd() {
      if (!(await this._validate())) {
        notify.error(
          "Form validation failed, please check your inputs and try again."
        );
        return Promise.reject();
      }

      const payload = { ...this.newRetainer };
      const startDate = new Date(this.newRetainer.start);

      if (startDate.getDate() !== 1) {
        try {
          await this.$swal({
            title: "Raise First Invoice?",
            html: "This retainer starts partway through the month. Would you like to raise the first invoice today, or wait until the 1st of next month?",
            type: "question",
            showCancelButton: true,
            confirmButtonText: "Raise Invoice Now",
            cancelButtonText: "Wait Until 1st",
          });
          payload.generate_invoice = true;
        } catch (e) {
          // User clicked "Wait Until 1st" — proceed without generating invoice
        }
      }

      try {
        return this.addSingle(payload)
          .then((response) => {
            this.close();
            this.$emit('created');
          })
          .catch((error) => {
            if (error.response.status === 422) {
              notify.error(
                "Form validation failed, please check your inputs and try again."
              );
            } else {
              console.error(error);
              notify.error("Failed to create retainer, please try again.");
            }
          });
      } catch (error) {
        console.error(error);
      }
    },

    async handleUpdate() {
      if (!(await this._validate())) {
        notify.error(
          "Form validation failed, please check your inputs and try again."
        );
        return Promise.reject();
      }

      try {
        return this.updateSingle({
          id: this.newRetainer.id,
          payload: this.newRetainer,
        })
          .then((response) => {
            this.setClient(response.data.client);

            notify.success("Retainer successfuly updated.");
            this.close();
            this.$emit("updated");
          })
          .catch((error) => {
            if (error.response.status === 422) {
              notify.error(
                "Form validation failed, please check your inputs and try again."
              );
            } else {
              console.error(error);
              notify.error("Failed to update retainer, please try again.");
            }
          });
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>
